import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchDistrictMillDistances, slugify } from './utils';
import { useTranslation } from '../../hooks/useTranslation';
import { localizedFactoryName } from '../../utils/localizedDisplayName';

// Ranked list of other mills in the same district, straight-line distance in
// km, nearest-first (per product decision, corrected Aug 13 2026). Top 10 shown by default with a
// "Show more" toggle to reveal the rest. Self-fetches via millId — no parent
// wiring needed beyond passing the id. Renders nothing if there are zero
// same-district mills with a usable pincode match (not an error state, just
// nothing to show).
const NearbyMillsDistanceList = ({ millId, millName, accentColor = '#f59e0b', titleColor = '#92400e' }) => {
  const { language } = useTranslation();
  const [distances, setDistances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { distances: rows } = await fetchDistrictMillDistances(millId);
      if (!cancelled) {
        setDistances(rows || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [millId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3">
        <div className="w-4 h-4 rounded-full border-2 animate-spin" style={{ borderColor: '#e5e7eb', borderTopColor: accentColor }} />
        <p className="text-xs" style={{ color: '#9ca3af' }}>Loading nearby mills…</p>
      </div>
    );
  }

  if (!distances || distances.length === 0) {
    return (
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: titleColor }}>
          {millName ? `Distance from ${millName} to other factories in this district` : 'Other mills in this district'}
        </p>
        <p className="text-xs" style={{ color: '#9ca3af' }}>No other mills found nearby yet.</p>
      </div>
    );
  }

  const maxKm = distances[distances.length - 1]?.distance_km || 1;
  const visible = showAll ? distances : distances.slice(0, 10);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: titleColor }}>
          {millName ? `Distance from ${millName} to other factories in this district` : 'Other mills in this district'}
        </p>
        <span className="text-[11px] flex-shrink-0" style={{ color: '#9ca3af' }}>Nearest first</span>
      </div>
      <div className="flex flex-col gap-2">
        {visible.map((row, i) => {
          const pct = Math.max(6, Math.round((Number(row.distance_km) / Number(maxKm)) * 100));
          return (
            <Link
              key={row.mill_id}
              to={`/market-intelligence/factory/${slugify(row.canonical_name)}`}
              className="flex items-start gap-3 px-3 py-2 rounded-lg transition-colors hover:bg-gray-50"
              style={{ border: '1px solid #e8ddd0', background: '#fff' }}
            >
              <span className="text-xs w-4 flex-shrink-0 mt-0.5" style={{ color: '#9ca3af' }}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold underline" style={{ color: '#2563eb' }}>{localizedFactoryName(row, language)}</p>
                <div className="h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: '#f3f4f6' }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accentColor }} />
                </div>
              </div>
              <span className="text-sm font-bold flex-shrink-0 mt-0.5" style={{ color: '#1f2937', minWidth: 56, textAlign: 'right' }}>
                {row.distance_km} km
              </span>
            </Link>
          );
        })}
      </div>
      {distances.length > 10 && (
        <button
          onClick={() => setShowAll((s) => !s)}
          className="mt-2 text-xs font-semibold underline"
          style={{ color: accentColor }}
        >
          {showAll ? 'Show less' : `Show ${distances.length - 10} more`}
        </button>
      )}
    </div>
  );
};

export default NearbyMillsDistanceList;

